import React, { useEffect } from 'react';
import {
  FileText,
  Shield,
  Music,
  Image as ImageIcon,
  AlertTriangle,
  Mail,
  Globe,
} from 'lucide-react';
import './TermsOfService.css';
import usePageTitle from '../hooks/usePageTitle';

const EFFECTIVE_DATE = 'June 3, 2026';

const TermsOfService: React.FC = () => {
  usePageTitle('Terms of Service - The Kults');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <div className="terms-container">
        <header className="terms-header">
          <div className="terms-icon">
            <FileText size={48} />
          </div>
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: {EFFECTIVE_DATE}</p>
        </header>

        <div className="terms-content">
          <section className="terms-section">
            <div className="terms-summary">
              <h2 className="terms-summary-title">The short version</h2>
              <ul>
                <li>
                  This is the official website of The Kults. Enjoy the music,
                  news, and content &mdash; just don&apos;t copy or misuse it.
                </li>
                <li>
                  Everything here (music, images, text, the band name) belongs
                  to us. You can view and share it, but not sell or repurpose it.
                </li>
                <li>
                  The site is provided &quot;as is.&quot; We do our best, but we
                  can&apos;t promise it&apos;s perfect or always available.
                </li>
                <li>
                  Questions? Reach us through our{' '}
                  <a href="/contact">contact form</a>.
                </li>
              </ul>
            </div>
          </section>

          <section className="terms-section">
            <h2>1. Agreement to these terms</h2>
            <p>
              By accessing or using thekults.com (the &quot;Website&quot;), you
              agree to these Terms of Service (&quot;Terms&quot;). If you
              don&apos;t agree, please don&apos;t use the Website.
            </p>
            <p>
              These Terms are a simple agreement between you and The Kults
              (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), an
              independent music band based in Basel, Switzerland.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. What this website is</h2>
            <p>
              This is our official site. It exists to share our music and
              connect with fans. Depending on what we&apos;ve published, it may
              include:
            </p>
            <ul>
              <li>Music and streaming links</li>
              <li>Tour and show information</li>
              <li>News and updates</li>
              <li>A photo and video gallery</li>
              <li>An optional newsletter</li>
              <li>A contact form</li>
              <li>Official merchandise (where available)</li>
            </ul>
            <p>
              Content may change or be removed at any time without notice. There
              are no user accounts &mdash; you can use the site without
              registering.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Using this website</h2>

            <h3>3.1 What you can do</h3>
            <p>You&apos;re welcome to use the Website for personal use, including:</p>
            <ul>
              <li>Browsing and enjoying our content</li>
              <li>Subscribing to or unsubscribing from our newsletter</li>
              <li>Contacting us through the official form</li>
              <li>Sharing our content through the links we provide</li>
            </ul>

            <h3>3.2 What you shouldn&apos;t do</h3>
            <p>Please don&apos;t:</p>
            <ul>
              <li>Use the Website for anything unlawful</li>
              <li>Try to gain unauthorized access to our systems</li>
              <li>Disrupt, overload, or interfere with the Website</li>
              <li>Upload or transmit malicious code</li>
              <li>Scrape or use automated systems to harvest content</li>
              <li>Copy, resell, or commercially exploit our content</li>
              <li>Misuse the contact form or newsletter (spam, abuse, etc.)</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. Intellectual property</h2>

            <div className="ip-grid">
              <div className="ip-item">
                <Music size={24} />
                <h4>Our Music</h4>
                <p>All recordings and songs are owned or licensed by The Kults.</p>
              </div>
              <div className="ip-item">
                <ImageIcon size={24} />
                <h4>Images &amp; Text</h4>
                <p>Photos, artwork, and written content are protected by copyright.</p>
              </div>
              <div className="ip-item">
                <Shield size={24} />
                <h4>Our Name</h4>
                <p>&quot;The Kults&quot; and our logos are our marks.</p>
              </div>
              <div className="ip-item">
                <FileText size={24} />
                <h4>Your Use</h4>
                <p>A personal, non-commercial license to view and enjoy.</p>
              </div>
            </div>

            <p>
              Everything on this Website &mdash; including music, images,
              artwork, text, and design &mdash; belongs to The Kults or our
              licensors and is protected by copyright and other laws.
            </p>
            <p>
              We grant you a limited, personal, non-commercial license to view
              and enjoy our content. You may not reproduce, sell, modify, or
              create derivative works from it, or remove any copyright notices,
              without our written permission.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Newsletter &amp; communications</h2>
            <p>
              If you subscribe to our newsletter, you&apos;re asking us to email
              you band news and updates. You can unsubscribe at any time using
              the link in any email or through our{' '}
              <a href="/contact">contact form</a>. How we handle your email is
              described in our <a href="/privacy">Privacy Policy</a>.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Merchandise &amp; orders</h2>
            <p>
              Where merchandise is offered, all items are subject to
              availability, and we may correct pricing errors or cancel orders
              for items that become unavailable.
            </p>
            <p>
              When we accept payments, they are handled by trusted third-party
              payment processors. We do not store your full card details on this
              Website. Any shipping, returns, and refund details will be shown
              at the time of purchase.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Third-party links &amp; services</h2>
            <p>
              Our Website may link to third-party sites and services (for
              example, streaming platforms or social media). We don&apos;t
              control those sites and aren&apos;t responsible for their content
              or practices. Your use of them is governed by their own terms and
              privacy policies.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Disclaimer</h2>

            <div className="liability-notice">
              <AlertTriangle size={32} />
              <h3>Provided &quot;as is&quot;</h3>
              <p>
                The Website and its content are provided &quot;as is&quot; and
                &quot;as available,&quot; without warranties of any kind. We
                don&apos;t guarantee the Website will always be accurate,
                complete, secure, or uninterrupted.
              </p>
            </div>

            <p>
              We may change, suspend, or discontinue any part of the Website at
              any time, including for maintenance or updates. Information may be
              updated or removed without notice.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, The Kults will not be
              liable for any indirect, incidental, or consequential damages
              arising from your use of (or inability to use) the Website.
            </p>
            <p>
              Nothing in these Terms limits any rights you have under mandatory
              consumer protection laws in your country that cannot legally be
              waived.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Changes to these terms</h2>
            <p>
              We may update these Terms from time to time. Changes take effect
              when we post them here and update the &quot;Last updated&quot;
              date. Continuing to use the Website after changes means you accept
              the updated Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Governing law</h2>
            <p>
              These Terms are governed by the laws of Switzerland, without regard
              to conflict-of-law rules. If you&apos;re a consumer in the EU, UK,
              or elsewhere, you keep the protections of any mandatory laws of
              your home country. We&apos;ll always try to resolve any concerns
              informally first &mdash; just reach out.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Contact us</h2>
            <p>Questions about these Terms? Get in touch:</p>

            <div className="contact-info">
              <div className="contact-item">
                <Mail size={20} />
                <strong>Contact form:</strong>
                <a href="/contact">Send us a message</a>
              </div>
              <div className="contact-item">
                <FileText size={20} />
                <strong>Privacy Policy:</strong>
                <a href="/privacy">Read our Privacy Policy</a>
              </div>
              <div className="contact-item">
                <Globe size={20} />
                <strong>Based in:</strong>
                <span>Basel, Switzerland</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
